/**
 * ac-comparar.js
 * Estado global del comparador (localStorage) + barra flotante + botones
 * "Comparar" de tarjeta/ficha. Sin dependencias externas. Se carga con defer
 * en todas las páginas vía snippets/ac-comparar-flotante.liquid.
 *
 * Lo consume también assets/ac-comparador.js (la página /pages/comparar),
 * que reutiliza acComparar.leer() / acComparar.suscribir().
 */
(function () {
  'use strict';

  var CLAVE = 'ac_comparar_v1';
  var MAX = 4;
  var memoria = null; // caché en memoria por si localStorage falla (modo privado, cuotas, etc.)
  var suscriptores = [];

  function leerAlmacenamiento() {
    try {
      var crudo = window.localStorage.getItem(CLAVE);
      if (!crudo) return [];
      var datos = JSON.parse(crudo);
      if (!Array.isArray(datos)) return [];
      return datos;
    } catch (e) {
      return [];
    }
  }

  function guardarAlmacenamiento(items) {
    try {
      window.localStorage.setItem(CLAVE, JSON.stringify(items));
    } catch (e) {
      // Cuota llena, modo privado, etc.: seguimos funcionando solo en memoria
      // para el resto de la sesión de navegación.
    }
  }

  function leer() {
    if (memoria === null) {
      memoria = leerAlmacenamiento();
    }
    return memoria.slice();
  }

  function guardar(items) {
    memoria = items.slice();
    guardarAlmacenamiento(memoria);
    suscriptores.forEach(function (fn) {
      try {
        fn(leer());
      } catch (e) {
        /* un suscriptor no debe romper a los demás */
      }
    });
  }

  function suscribir(fn) {
    suscriptores.push(fn);
    return function desuscribir() {
      suscriptores = suscriptores.filter(function (f) {
        return f !== fn;
      });
    };
  }

  function estaEnLista(handle) {
    return leer().some(function (p) {
      return p.handle === handle;
    });
  }

  function agregar(producto) {
    var items = leer();
    if (items.some(function (p) { return p.handle === producto.handle; })) {
      return { ok: true, items: items };
    }
    if (items.length >= MAX) {
      return { ok: false, motivo: 'max', items: items };
    }
    items.push(producto);
    guardar(items);
    return { ok: true, items: items };
  }

  function quitar(handle) {
    var items = leer().filter(function (p) {
      return p.handle !== handle;
    });
    guardar(items);
    return items;
  }

  function vaciar() {
    guardar([]);
  }

  function tiposMixtos(items) {
    var tipos = {};
    items.forEach(function (p) {
      if (p.type) tipos[p.type] = true;
    });
    return Object.keys(tipos).length > 1;
  }

  window.acComparar = {
    leer: leer,
    agregar: agregar,
    quitar: quitar,
    vaciar: vaciar,
    estaEnLista: estaEnLista,
    tiposMixtos: tiposMixtos,
    suscribir: suscribir,
    MAX: MAX
  };

  // --- Botones "Comparar" en tarjetas y ficha (delegación de eventos) ---

  function datosDesdeBoton(boton) {
    var variantId = boton.getAttribute('data-variant-id');
    return {
      handle: boton.getAttribute('data-handle') || '',
      title: boton.getAttribute('data-title') || '',
      vendor: boton.getAttribute('data-vendor') || '',
      type: boton.getAttribute('data-type') || '',
      price: Number(boton.getAttribute('data-price')) || 0,
      image: boton.getAttribute('data-image') || '',
      url: boton.getAttribute('data-url') || '',
      variantId: variantId ? Number(variantId) : null
    };
  }

  function actualizarBoton(boton) {
    var handle = boton.getAttribute('data-handle');
    var activo = estaEnLista(handle);
    boton.setAttribute('aria-pressed', activo ? 'true' : 'false');
    boton.classList.toggle('ac-comparar-boton--activo', activo);
    var textoAgregar = boton.querySelector('[data-ac-texto-agregar]');
    var textoQuitar = boton.querySelector('[data-ac-texto-quitar]');
    if (textoAgregar) textoAgregar.hidden = activo;
    if (textoQuitar) textoQuitar.hidden = !activo;
  }

  function actualizarTodosLosBotones() {
    var botones = document.querySelectorAll('[data-ac-comparar-boton]');
    for (var i = 0; i < botones.length; i++) {
      actualizarBoton(botones[i]);
    }
  }

  function mostrarAvisoEnBoton(boton, mensaje) {
    var aviso = boton.querySelector('.ac-comparar-boton__aviso-inline');
    if (!aviso) {
      aviso = document.createElement('span');
      aviso.className = 'ac-comparar-boton__aviso-inline visually-hidden';
      aviso.setAttribute('role', 'status');
      boton.appendChild(aviso);
    }
    aviso.textContent = mensaje;
    aviso.classList.remove('visually-hidden');
    window.setTimeout(function () {
      aviso.classList.add('visually-hidden');
    }, 4000);
  }

  document.addEventListener('click', function (evento) {
    var boton = evento.target.closest && evento.target.closest('[data-ac-comparar-boton]');
    if (!boton) return;
    evento.preventDefault();
    evento.stopPropagation();

    var handle = boton.getAttribute('data-handle');
    if (estaEnLista(handle)) {
      quitar(handle);
      return;
    }
    var resultado = agregar(datosDesdeBoton(boton));
    if (!resultado.ok && resultado.motivo === 'max') {
      mostrarAvisoEnBoton(
        boton,
        'Ya tiene ' + MAX + ' productos para comparar. Quite alguno antes de agregar otro.'
      );
    }
  });

  // --- Barra flotante ---

  function crearMiniatura(producto) {
    var li = document.createElement('li');
    li.className = 'ac-comparar-barra__miniatura';

    if (producto.image) {
      var img = document.createElement('img');
      img.src = producto.image;
      img.alt = producto.title || '';
      img.loading = 'lazy';
      img.width = 40;
      img.height = 40;
      li.appendChild(img);
    }

    var nombre = document.createElement('span');
    nombre.className = 'ac-comparar-barra__miniatura-nombre';
    nombre.textContent = producto.title || '';
    li.appendChild(nombre);

    var quitarBtn = document.createElement('button');
    quitarBtn.type = 'button';
    quitarBtn.className = 'ac-comparar-barra__quitar';
    quitarBtn.setAttribute('data-ac-quitar', producto.handle);
    quitarBtn.setAttribute('aria-label', 'Quitar ' + (producto.title || 'producto') + ' de comparar');
    quitarBtn.textContent = '×';
    li.appendChild(quitarBtn);

    return li;
  }

  function renderizarBarra() {
    var barra = document.querySelector('[data-ac-comparar-barra]');
    if (!barra) return;
    var items = leer();

    if (items.length === 0) {
      barra.hidden = true;
      return;
    }
    barra.hidden = false;

    var contador = barra.querySelector('[data-ac-contador]');
    if (contador) contador.textContent = String(items.length);

    var lista = barra.querySelector('[data-ac-miniaturas]');
    if (lista) {
      lista.innerHTML = '';
      items.forEach(function (producto) {
        lista.appendChild(crearMiniatura(producto));
      });
    }

    var aviso = barra.querySelector('[data-ac-aviso]');
    if (aviso) {
      if (tiposMixtos(items)) {
        aviso.hidden = false;
        aviso.textContent =
          'Está comparando productos de distinto tipo. Algunas filas técnicas podrían no aplicar a todos.';
      } else {
        aviso.hidden = true;
        aviso.textContent = '';
      }
    }

    var verBtn = barra.querySelector('[data-ac-ver]');
    if (verBtn) {
      verBtn.setAttribute(
        'aria-disabled',
        items.length < 2 ? 'true' : 'false'
      );
    }
  }

  document.addEventListener('click', function (evento) {
    var quitarBtn = evento.target.closest && evento.target.closest('[data-ac-quitar]');
    if (quitarBtn) {
      quitar(quitarBtn.getAttribute('data-ac-quitar'));
      return;
    }
    var vaciarBtn = evento.target.closest && evento.target.closest('[data-ac-vaciar]');
    if (vaciarBtn) {
      vaciar();
    }
  });

  suscribir(function () {
    actualizarTodosLosBotones();
    renderizarBarra();
  });

  document.addEventListener('DOMContentLoaded', function () {
    actualizarTodosLosBotones();
    renderizarBarra();
  });

  // Si el DOM ya está listo cuando corre este script (defer + posición al final).
  if (document.readyState !== 'loading') {
    actualizarTodosLosBotones();
    renderizarBarra();
  }
})();

// Añadido sep-2026: solo comparar productos de la misma categoría (Tipo de producto)
!function(){"use strict";document.addEventListener("click",function(a){var t=a.target.closest&&a.target.closest("[data-ac-comparar-boton]");if(t&&window.acComparar){var e=t.getAttribute("data-handle");if(!window.acComparar.estaEnLista(e)){var o=window.acComparar.leer();if(o.length){var r=t.getAttribute("data-type")||"",i=o[0].type||"";r&&i&&r!==i&&(a.preventDefault(),a.stopImmediatePropagation(),function(a,t){var e=a.querySelector(".ac-comparar-boton__aviso-inline");e||((e=document.createElement("span")).className="ac-comparar-boton__aviso-inline visually-hidden",e.setAttribute("role","status"),a.appendChild(e)),e.textContent=t,e.classList.remove("visually-hidden"),window.setTimeout(function(){e.classList.add("visually-hidden")},5e3)}(t,"Solo puede comparar productos de la misma categor\xeda. Su comparaci\xf3n actual es de \xab"+i+"\xbb. Vac\xedela o quite esos productos para comparar \xab"+r+"\xbb."))}}}},!0)}();